import React, { useState } from 'react';
import cx from 'classnames';
import { ErrorMessage, Field, FieldArray } from 'formik';
import { Row, Col } from 'react-bootstrap';

import { formatPrice } from 'utilities/money';

import { MultiConfirmationFormGroupProps } from '@payments/types';
import { calculateSubtotal, calculateSum } from '@payments/utils';

import style from '@payments/style.module.scss';

/**
 * For displaying and editing the item totals for a purchase of multiple items (e.g. bulk orders).
 * Doesn't support coupons yet!
 */
export const MultiConfirmationFormGroup: React.FC<MultiConfirmationFormGroupProps> = ({
  values,
  errors,
  requirePersonalizedPackage,
  variant,
  className
}) => {
  const pricing = variant === 'pricing';
  const [editable, setEditable] = useState(values.orderItems.map(() => pricing));
  const totalPrice = formatPrice(
    calculateSum(
      values.orderItems.map((item) => calculateSubtotal(item.purchasable.priceCents, item.purchasableCount))
    ),
    values.orderItems[0].purchasable.currency
  );

  return (
    <div className={cx('form-group', style.multipleSubscriptionsForm, className)}>
      <div className="text-danger">
        {errors &&
          errors.itemTotal &&
          errors.itemTotal.map((errorMessage) => <span key={errorMessage}>{errorMessage}</span>)}
      </div>
      <FieldArray
        name="orderItems"
        render={() => (
          <ul>
            {values.orderItems.map((item, index) => (
              <li key={index}>
                <div className="text-danger">
                  <ErrorMessage name={`orderItems.${index}.purchasableCount`} />
                </div>
                <Row>
                  <Col xs={6} md={6}>
                    <strong>{item.purchasable.slug.toUpperCase()} Accounts</strong>
                  </Col>
                  <Col xs={6} md={6} className={style.colRightAlign}>
                    <small>{formatPrice(item.purchasable.priceCents, item.purchasable.currency)} x </small>{' '}
                    <Field
                      name={`orderItems.${index}.purchasableCount`}
                      type="number"
                      min={0}
                      className={cx(style.miniStringInput, !editable[index] ? style.hidden : '')}
                    />
                    <strong>
                      <span className={editable[index] ? style.hidden : ''}>{item.purchasableCount}</span>
                    </strong>
                  </Col>
                  {!pricing && (
                    <Col xs={2} md={2} xsOffset={12} mdOffset={12} className={style.colEditBtn}>
                      <a
                        onClick={() => {
                          setEditable(editable.map((value, pos) => (pos === index ? !value : value)));
                        }}>
                        {!editable[index] ? 'Edit' : 'Save'}
                      </a>
                    </Col>
                  )}
                </Row>
                <Row>
                  <Col xs={6} md={6} xsOffset={6} mdOffset={6} className={style.colRightAlign}>
                    <div className={requirePersonalizedPackage ? style.visibilityHidden : ''}>
                      <small>
                        {' ='}
                        {formatPrice(
                          calculateSubtotal(item.purchasable.priceCents, item.purchasableCount),
                          item.purchasable.currency
                        )}
                      </small>
                    </div>
                  </Col>
                </Row>
              </li>
            ))}
          </ul>
        )}
      />

      <Row>
        <Col xs={4} md={4}>
          <strong>TOTAL</strong>
        </Col>
        <Col xs={8} md={8} className={style.colRightAlign}>
          {requirePersonalizedPackage ? 'Personalized package' : totalPrice}
        </Col>
      </Row>
    </div>
  );
};

export default MultiConfirmationFormGroup;
